import React, { Component } from 'react'
import PropTypes from 'prop-types'

import ConnectedToPractice from './ConnectedToPractice'
import PracticeTable from './PracticeTable'
import { Button } from 'antd'
import {
  addPracticeToRecents,
  getRecentPracticeIds,
  getRecentPractices,
  clearRecents,
  clearPracticeStatsData,
  getPractices,
} from '../services/practiceStorage'
import { practiceStorage } from '../services/localStorage'
import {
  getUserRoles,
  listDashboards,
  listDashboardsRecents,
  reassignUserToNewPractice,
  removeUserFromPractice,
  updatePractice,
} from '../common/requests'
import css from './Dashboard.css'
import PracticeFilters from './Filters'

class Dashboard extends Component {
  static propTypes = {
    signout: PropTypes.func.isRequired,
    editPractice: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    recentsLoading: PropTypes.bool.isRequired,
    toggleLoading: PropTypes.func.isRequired,
    toggleRecentsLoading: PropTypes.func.isRequired,
    isTopsAdmin: PropTypes.bool.isRequired,
    updateSearchString: PropTypes.func.isRequired,
    updateProducts: PropTypes.func.isRequired,
    updatePmsTypes: PropTypes.func.isRequired,
    updateActiveOnly: PropTypes.func.isRequired,
    searchFilters: PropTypes.object.isRequired,
  }

  state = {
    recentPracticeData: [],
    practicesStatsAndProducts: [],
    practiceConnected: {
      name: null,
      connected: false,
    },
  }

  componentDidMount() {
    const { searchFilters: { searchString, products, pmsTypes, activeOnly }, searchPractices } = this.props
    if (searchString.length || products.length || pmsTypes.length || activeOnly) {
      this.findConnectedPractice()
      this.fetchRecentPractices().then(() => searchPractices())
      return
    }

    clearPracticeStatsData()
    this.fetchRecentPractices().then(() => {
      this.loadDataFast().then(() => {
        this.findConnectedPractice()
        // this.loadDataSlow()
      })
    })
  }

  componentDidUpdate({ loading, recentsLoading }) {
    const { loading: nextLoading, recentsLoading: nextRecentsLoading } =
      this.props
    if (!nextLoading && loading) {
      this.setState({ practicesStatsAndProducts: getPractices() })
    } else if (!nextRecentsLoading && recentsLoading) {
      this.setState({ recentPracticeData: getRecentPractices() })
    }
  }

  fetchRecentPractices = () => {
    const { toggleRecentsLoading } = this.props
    const practiceIds = getRecentPracticeIds()
    if (practiceIds.length) {
      toggleRecentsLoading(true)
      return listDashboardsRecents(practiceIds)
    }
    return Promise.resolve()
  }

  loadDataSlow = () => {
    this.props.toggleLoading(true)
    listDashboards({ stats: true })
  }

  loadDataFast = () => {
    const { toggleLoading, signout } = this.props
    toggleLoading(true)
    return listDashboards({ stats: false })
      .then((res) => {
        practiceStorage.set(JSON.stringify(res))
        this.setState({ practicesStatsAndProducts: res })
        toggleLoading(false)
      })
      .catch((err) => {
        toggleLoading(false)
        if (err.status === 401) {
          signout()
        }
      })
  }

  findConnectedPractice() {
    const { practicesStatsAndProducts } = this.state
    getUserRoles().then((res) => {
      const practiceAdminRole = res.roles.find(role => role.name === 'practice_admin')
      if (practiceAdminRole) {
        const practiceData = practicesStatsAndProducts.find(psp => psp.practice.id === practiceAdminRole.resource_id)
        this.setState({
          practiceConnected: practiceData
            ? { ...practiceData.practice, connected: true }
            : { connected: false, name: null },
        })
      }
    })
  }

  disconnectFromPractice = () => {
    const {
      practiceConnected: { id },
    } = this.state
    removeUserFromPractice(id).then(() => {
      this.setState({
        practiceConnected: {
          name: null,
          connected: false,
        },
      })
    })
  }

  updateConnectedPractice = (practiceConnected) => {
    this.setState({
      practiceConnected: { ...practiceConnected, connected: true },
    })
  }

  updateData = (practiceId, nextOnboardingVal) =>
    updatePractice(practiceId, { onboarding: nextOnboardingVal })
      .then((res) => {
        const { practicesStatsAndProducts } = this.state

        const nextData = practicesStatsAndProducts.map((entry) => {
          if (entry.practice.id !== practiceId) {
            return entry
          }
          return {
            ...entry,
            practice: {
              ...entry.practice,
              onboarding: res.practice.onboarding,
            },
          }
        })
        this.fetchRecentPractices()
        this.setState({ practicesStatsAndProducts: nextData })
      })
      .catch((err) => {
        if (err.body.JSONerror === 'unauthorized') {
          this.props.signout()
        } else {
          const msg = `Could not update practice\n${err.message}`
          window.alert(msg)
          Promise.reject(msg)
        }
      })

  loginToPractice = (practice) => {
    addPracticeToRecents(practice.id)
    this.fetchRecentPractices()
    reassignUserToNewPractice(practice.id)
      .then(() => {
        this.updateConnectedPractice(practice)
        window.open(process.env.REACT_APP_CLIENT_BASE_URL, 'RoosterReminders')
      })
      .catch(err => console.error(err))
  }

  editPractice = (practiceId) => {
    addPracticeToRecents(practiceId)
    this.props.editPractice(practiceId)
  }

  handleClear = () => {
    clearRecents()
    this.setState({ recentPracticeData: [] })
  }

  render() {
    const {
      practiceConnected: { connected, name },
      practicesStatsAndProducts,
      recentPracticeData,
    } = this.state
    const {
      recentsLoading,
      loading,
      isTopsAdmin,
      updateSearchString,
      updateProducts,
      updatePmsTypes,
      updateActiveOnly,
      searchFilters,
    } = this.props
    const numPractices = practicesStatsAndProducts.length

    return (
      <div className={css.container}>
        {recentPracticeData.length ? (
          <div className={css.recents}>
            <div className={css.recentsTopRow}>
              <h2>Recent Practices</h2>
              <Button onClick={this.handleClear} className={css.clearRecents}>
                Clear
              </Button>
            </div>
            <PracticeTable
              practicesStatsAndProducts={recentPracticeData}
              editPractice={this.editPractice}
              loading={recentsLoading}
              loginToPractice={this.loginToPractice}
              isTopsAdmin={isTopsAdmin}
              updateData={this.updateData}
              recents
            />
          </div>
        ) : null}
        {connected && (
          <ConnectedToPractice
            name={name}
            handleDisconnect={this.disconnectFromPractice}
          />
        )}
        <PracticeFilters
          isTopsAdmin={isTopsAdmin}
          searchFilters={searchFilters}
          numPractices={numPractices}
          updateSearchString={updateSearchString}
          updateProducts={updateProducts}
          updatePmsTypes={updatePmsTypes}
          updateActiveOnly={updateActiveOnly}
        />
        <PracticeTable
          practicesStatsAndProducts={practicesStatsAndProducts}
          editPractice={this.editPractice}
          isTopsAdmin={isTopsAdmin}
          loading={loading}
          loginToPractice={this.loginToPractice}
          updateData={this.updateData}
          recents={false}
        />
      </div>
    )
  }
}

export default Dashboard



// WEBPACK FOOTER //
// ./src/Dashboard/index.js